<template>
    <div>
        <b-row>
            <b-col md="3">
                <b-overlay
                    :show="status.costTags === 'loading'"
                >
                    <b-card
                        no-body
                    >
                        <div class="card-header">
                            <div
                                class="card-group-title"
                            >
                                <h4>Tags</h4>
                            </div>
                        </div>
                        <div
                            class="card-body"
                        >
                            <p>
                                <b-badge variant="secondary">
                                    <i class="fas fa-tag mr-25"></i> {{ 'TAG:' + tagKey.substring(5) }}
                                </b-badge>
                            </p>
                            <b-form-radio-group
                                v-model="tagKey"
                                :options="tagOptions"
                                class="tagkey-radio-group"
                                value-field="value"
                                text-field="label"
                                stacked
                            />
                        </div>
                    </b-card>
                </b-overlay>
            </b-col>
            <b-col md="9">
                <b-row>
                    <b-col md="4">
                        <b-overlay
                            :show="status.costTags === 'loading'"
                        >
                            <CostTagsPieChart
                                :pieChartData="pieChartData"
                                tagKey="tagValue"
                                valueName="cost"
                                :theme="theme"
                                :themeColors="themeColors"
                            />
                        </b-overlay>
                    </b-col>
                    <b-col md="8">
                        <b-overlay
                            :show="status.costTags === 'loading'"
                        >
                            <CostTagsLineChart
                                :month="month"
                                :lineChartData="lineChartData"
                                :pieChartData="pieChartData"
                                :yKeys="fullKeys"
                                :isLoading="isLoading"
                                xKey="date"
                                :theme="theme"
                                :themeColors="themeColors"
                            />
                        </b-overlay>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <b-overlay
                            :show="status.costTags === 'loading'"
                        >
                            <CostTagsTreeList
                                :data="treeData"
                                :month="month"
                                :projectIdx="project.projectIdx"
                                :tagKey="tagKey"
                                @api:getCostTagsByCategory="getCostTagsByCategory"
                                @api:getCostTagsByResourceId="getCostTagsByResourceId"
                            ></CostTagsTreeList>
                        </b-overlay>
                    </b-col>
                </b-row>
            </b-col>
        </b-row>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { inject } from '@vue/composition-api'
import CostTagsLineChart from './_components/CostTagsLineChart'
import CostTagsPieChart from './_components/CostTagsPieChart'
import CostTagsTreeList from './_components/CostTagsTreeList'
export default {
    name: 'CostsByTagModule',
    props: ['project', 'month'],
    data () {
        return {
            viewCount: 4,
            tagKey: 'user:Name',
            open: false
        }
    },
    components: {
        CostTagsLineChart,
        CostTagsPieChart,
        CostTagsTreeList
    },
    setup (props, context) {
        const themeColors = inject('themeColors')
        const theme = inject('theme')
        return {
            theme,
            themeColors
        }
    },
    computed: {
        ...mapGetters({
            dailyTagValues: '$_costsByTag/dailyTagValues',
            tagValues: '$_costsByTag/tagValues',
            treeData: '$_costsByTag/treeData',
            tags: '$_costsByTag/tags',
            status: '$_costsByTag/status',
            availableTags: '$_costsByTag/availableTags'
        }),
        tagOptions () {
            return this.availableTags.map(tag => {
                return {
                    label: tag.substring(5),
                    value: tag
                }
            })
        },
        fullKeys () {
            return this.pieChartData.map(item => item.tagValue)
        },
        isLoading () {
            return this.status.costTags === 'loading'
        },
        noData () {
            return (this.dailyTagValues.length === 0 && this.tagValues.length === 0)
        },
        checkIfNeedsEtcData () {
            return this.tagValues.length > this.viewCount + 1
        },
        lineChartData () {
            const keyArrOfChartData = this.checkIfNeedsEtcData
                ? this.tagValues.slice(0, this.viewCount).map(item => item.tagValue).concat(['ETC'])
                : this.tagValues.slice().map(item => item.tagValue)

            return this.dailyTagValues.map(item => {
                // This is always contains key for N/A
                const tagValues = item.tagValues.reduce((accumulator, currentValue) => {
                    if (!accumulator.ETC) accumulator.ETC = null
                    if (keyArrOfChartData.indexOf(currentValue.tagValue) > -1) {
                        accumulator[currentValue.tagValue] = currentValue.cost
                    } else {
                        accumulator.ETC = Number(accumulator.ETC) + currentValue.cost
                    }
                    return accumulator
                }, { ...this.setObjectKeyByArrValues(keyArrOfChartData) })
                return {
                    date: item.date,
                    ...tagValues
                }
            })
        },
        pieChartData () {
            let fullData = []
            if (this.checkIfNeedsEtcData) {
                const etcDataForPieChart = this.tagValues.slice(this.viewCount, this.tagValues.length).reduce((accumulator, currentValue) => {
                    accumulator.cost += currentValue.cost
                    return accumulator
                }, { tagValue: 'ETC', cost: 0 })
                fullData = this.tagValues.slice(0, this.viewCount).concat(etcDataForPieChart)
            } else {
                fullData = this.tagValues.slice()
            }
            return fullData.map(this._setCostFormat).sort(this._sorter)
        }
    },
    methods: {
        ...mapActions({
            getCostTags: '$_costsByTag/getCostTags',
            getCostTagsByCategory: '$_costsByTag/getCostTagsByCategory',
            getCostTagsByResourceId: '$_costsByTag/getCostTagsByResourceId'
        }),
        setObjectKeyByArrValues (arr) {
            return arr.slice().reduce((accumulator, currentValue) => {
                if (this.tagValues.filter(item => item.tagValue === currentValue).length > 0) {
                    accumulator[currentValue] = null
                }
                return accumulator
            }, {})
        },
        showDescription () {
            this.open = !this.open
        },
        _sorter (a, b) {
            if (a.tagValue === 'ETC' || b.tagValue === 'ETC') return 0
            return b.cost - a.cost
        },
        _setCostFormat (item) {
            return {
                ...item,
                label: this.$_getDollar(item.cost)
            }
        }
    },
    watch: {
        project: {
            handler (value, oldValue) {
                this.getCostTags({ projectIdx: value.projectIdx, month: this.month, tagKey: this.tagKey })
            },
            deep: true
        },
        month (value, oldValue) {
            this.getCostTags({ projectIdx: this.project.projectIdx, month: value, tagKey: this.tagKey })
        },
        tagKey (tagKey) {
            this.getCostTags({ projectIdx: this.project.projectIdx, month: this.month, tagKey })
        }
    }
}
</script>

<style lang="scss" scoped>
.tagkey-radio-group {
    ::v-deep > .custom-control {
        margin-bottom: 0.75rem;
    }
}
</style>
